import { useEffect, useState } from "react";
import {
  adminGetProductTypes,
  adminCreateProductType,
  adminUpdateProductType,
  adminDeleteProductType,
  adminGetCategories,
} from "../../utils/api";
import "./AdminPage.css";

const EMPTY = { name: "", hasSizes: false, hasTextures: false, categoryId: "" };

export default function AdminProductTypes() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    adminGetCategories().then((r) => setCategories(r.data.data || []));
  }, []);

  const load = () => {
    setLoading(true);
    adminGetProductTypes()
      .then((r) => setTypes(r.data.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setError("");
    setModal(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name,
      hasSizes: t.hasSizes || false,
      hasTextures: t.hasTextures || false,
      categoryId: t.categoryId || "",
    });
    setError("");
    setModal(true);
  };

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError("Назва типу обов'язкова");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        name: form.name,
        hasSizes: form.hasSizes,
        hasTextures: form.hasTextures,
        categoryId: form.categoryId,
      };
      if (editing) await adminUpdateProductType(editing.id, payload);
      else await adminCreateProductType(payload);
      setModal(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || "Помилка збереження");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Видалити цей тип товару?")) return;
    try {
      await adminDeleteProductType(id);
      load();
    } catch {
      alert("Помилка видалення");
    }
  };

  return (
    <div>
      <div className="admin-header">
        <h1>Типи товарів</h1>
        <button className="admin-btn primary" onClick={openCreate}>
          + Додати тип
        </button>
      </div>

      {loading ? (
        <div className="spinner" />
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Назва</th>
                <th>Категорія</th>
                <th>Розміри</th>
                <th>Текстури</th>
                <th>Дата</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {types.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    style={{ textAlign: "center", padding: 40, color: "var(--text-light)" }}
                  >
                    Типів товарів немає
                  </td>
                </tr>
              ) : (
                types.map((t) => (
                  <tr key={t.id}>
                    <td>
                      <strong>{t.name}</strong>
                    </td>
                    <td>{t.category ? t.category.name : "—"}</td>
                    <td>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "2px 10px",
                          borderRadius: 12,
                          fontSize: "0.8rem",
                          fontWeight: 600,
                          background: t.hasSizes ? "#dcfce7" : "#f1f5f9",
                          color: t.hasSizes ? "#16a34a" : "#64748b",
                        }}
                      >
                        {t.hasSizes ? "✓ Так" : "— Ні"}
                      </span>
                    </td>
                    <td>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "2px 10px",
                          borderRadius: 12,
                          fontSize: "0.8rem",
                          fontWeight: 600,
                          background: t.hasTextures ? "#dbeafe" : "#f1f5f9",
                          color: t.hasTextures ? "#2563eb" : "#64748b",
                        }}
                      >
                        {t.hasTextures ? "✓ Так" : "— Ні"}
                      </span>
                    </td>
                    <td>{new Date(t.createdAt).toLocaleDateString("uk-UA")}</td>
                    <td>
                      <div className="admin-table-actions">
                        <button className="admin-btn secondary" onClick={() => openEdit(t)}>
                          Редагувати
                        </button>
                        <button className="admin-btn danger" onClick={() => handleDelete(t.id)}>
                          Видалити
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div
          className="admin-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setModal(false);
          }}
        >
          <div className="admin-modal">
            <h2>{editing ? "Редагувати тип товару" : "Новий тип товару"}</h2>
            <form onSubmit={handleSubmit}>
              <div className="admin-form-group">
                <label>Категорія</label>
                <select name="categoryId" value={form.categoryId} onChange={handleChange}>
                  <option value="">Без категорії</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="admin-form-group">
                <label>Назва *</label>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Наприклад: Одяг, Взуття, Аксесуари"
                  required
                />
              </div>

              <div className="admin-form-group">
                <label
                  style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}
                >
                  <input
                    name="hasSizes"
                    type="checkbox"
                    checked={form.hasSizes}
                    onChange={handleChange}
                    style={{ width: 18, height: 18, cursor: "pointer" }}
                  />
                  <span>Товари цього типу мають розміри (S/M/L або кастомні)</span>
                </label>
                {form.hasSizes && (
                  <p
                    style={{
                      marginTop: 8,
                      fontSize: "0.8rem",
                      color: "var(--text-light)",
                      background: "#f0fdf4",
                      border: "1px solid #bbf7d0",
                      borderRadius: 6,
                      padding: "6px 10px",
                    }}
                  >
                    💡 При створенні товару з цим типом з'явиться поле для вибору розмірів
                  </p>
                )}
              </div>
              <div className="admin-form-group">
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    cursor: "pointer",
                  }}
                >
                  <input
                    name="hasTextures"
                    type="checkbox"
                    checked={form.hasTextures}
                    onChange={handleChange}
                    style={{
                      width: 18,
                      height: 18,
                      cursor: "pointer",
                    }}
                  />

                  <span>Товари цього типу мають текстури</span>
                </label>

                {form.hasTextures && (
                  <p
                    style={{
                      marginTop: 8,
                      fontSize: "0.8rem",
                      color: "var(--text-light)",
                      background: "#eff6ff",
                      border: "1px solid #bfdbfe",
                      borderRadius: 6,
                      padding: "6px 10px",
                    }}
                  >
                    💡 При створенні товару з'явиться можливість додавати текстури з ціною та фото
                  </p>
                )}
              </div>

              {error && (
                <p style={{ color: "#dc2626", fontSize: "0.85rem", marginBottom: 8 }}>{error}</p>
              )}

              <div className="admin-form-actions">
                <button
                  type="button"
                  className="admin-btn secondary"
                  onClick={() => setModal(false)}
                >
                  Скасувати
                </button>
                <button type="submit" className="admin-btn primary" disabled={submitting}>
                  {submitting ? "Збереження..." : editing ? "Зберегти" : "Створити"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
